<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from 'vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  currentOwner: { type: Object, default: null },
  agents: { type: Array, default: () => [] },
  managerMode: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
  initialTargetAssigneeId: { type: [String, Number], default: null },
  // 'carteirizacao' altera o dono do lead; 'atendimento' altera quem conduz o
  // atendimento agora. São ações distintas por regra de negócio, então os
  // textos precisam deixar claro qual delas está em jogo — o mesmo formulário
  // serve às duas porque a mecânica (escolher pessoa + justificar) é idêntica.
  kind: {
    type: String,
    default: 'carteirizacao',
    validator: value => ['carteirizacao', 'atendimento'].includes(value),
  },
});

const emit = defineEmits(['close', 'submit']);

const COPY = {
  carteirizacao: {
    managerTitle: 'Alterar carteirização',
    requestTitle: 'Solicitar alteração de carteirização',
    subtitle:
      'Esta ação altera o dono dos próximos atendimentos, não o atendente atual.',
    currentLabel: 'Dono atual',
    currentEmpty: 'Sem dono definido',
    targetLabel: 'Novo dono',
    justificationPlaceholder:
      'Explique por que o dono permanente do lead deve ser alterado.',
    managerSubmit: 'Alterar dono',
  },
  atendimento: {
    managerTitle: 'Transferir atendimento',
    requestTitle: 'Solicitar transferência de atendimento',
    subtitle:
      'Esta ação altera apenas quem conduz o atendimento agora. O dono do lead permanece o mesmo.',
    currentLabel: 'Atendendo agora',
    currentEmpty: 'Sem responsável definido',
    targetLabel: 'Novo responsável',
    justificationPlaceholder:
      'Explique por que este atendimento deve passar para outro vendedor.',
    managerSubmit: 'Transferir',
  },
};

const copy = computed(() => COPY[props.kind] || COPY.carteirizacao);

const targetAssigneeId = ref('');
const justification = ref('');

const agentOptions = computed(() =>
  props.agents.map(agent => ({
    label: agent.name,
    value: Number(agent.id),
  }))
);
const normalizedJustification = computed(() => justification.value.trim());
const canSubmit = computed(
  () =>
    Number(targetAssigneeId.value) > 0 &&
    (props.managerMode ||
      (normalizedJustification.value.length >= 5 &&
        normalizedJustification.value.length <= 500)) &&
    !props.submitting
);
const submitLabel = computed(() => {
  if (props.managerMode) {
    return props.submitting ? 'Salvando…' : copy.value.managerSubmit;
  }
  return props.submitting ? 'Enviando…' : 'Enviar solicitação';
});

watch(
  () => props.show,
  show => {
    if (!show) return;
    targetAssigneeId.value = props.initialTargetAssigneeId || '';
    justification.value = '';
  }
);

const submit = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    targetAssigneeId: Number(targetAssigneeId.value),
    justification: props.managerMode ? '' : normalizedJustification.value,
  });
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      @click.self="emit('close')"
    >
      <div class="w-full max-w-lg rounded-xl bg-n-solid-1 p-5 shadow-xl">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ managerMode ? copy.managerTitle : copy.requestTitle }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-10">
              {{ copy.subtitle }}
            </p>
          </div>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
            aria-label="Fechar"
            :disabled="submitting"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="mt-5 rounded-lg bg-n-alpha-1 px-3 py-2">
          <p class="text-xs font-medium uppercase tracking-wide text-n-slate-9">
            {{ copy.currentLabel }}
          </p>
          <p class="mt-1 text-sm font-medium text-n-slate-12">
            {{ currentOwner?.name || copy.currentEmpty }}
          </p>
        </div>

        <p class="mt-4 text-sm font-medium text-n-slate-12">
          {{ copy.targetLabel }}
        </p>
        <ComboBox
          v-model="targetAssigneeId"
          :options="agentOptions"
          placeholder="Selecione um vendedor"
          search-placeholder="Pesquisar vendedor…"
          empty-state="Nenhum vendedor encontrado"
          class="mt-2 [&>div>button]:h-10"
          :disabled="submitting"
        />

        <div
          v-if="!managerMode"
          class="mt-4 flex items-center justify-between gap-3"
        >
          <label
            class="text-sm font-medium text-n-slate-12"
            for="acl-ownership-justification"
          >
            Justificativa obrigatória
          </label>
          <span class="text-xs text-n-slate-9"
            >{{ justification.length }}/500</span
          >
        </div>
        <textarea
          v-if="!managerMode"
          id="acl-ownership-justification"
          v-model="justification"
          rows="4"
          maxlength="500"
          :placeholder="copy.justificationPlaceholder"
          class="mt-2 w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-8 focus:outline-none"
          :disabled="submitting"
        />
        <p
          v-if="
            !managerMode &&
            normalizedJustification.length > 0 &&
            normalizedJustification.length < 5
          "
          class="mt-1 text-xs text-n-ruby-10"
        >
          Informe pelo menos 5 caracteres.
        </p>

        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="h-9 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-1"
            :disabled="submitting"
            @click="emit('close')"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="h-9 rounded-lg bg-n-blue-9 px-4 text-sm font-semibold text-white hover:bg-n-blue-10 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canSubmit"
            @click="submit"
          >
            {{ submitLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
